// Star system tiers (04 §3.3, V-2) and the galaxy map (04 §5.1). One strict schema per tier: a
// lower tier cannot carry a field of a higher one.
import { z } from 'zod';
import { allianceEmblemSchema } from './alliances.js';
import { ownerClaimSchema } from './claims.js';
import {
  facilityTypeSchema,
  idSchema,
  isoDateTimeSchema,
  shipCountsSchema,
  shipTypeSchema,
  systemTypeSchema,
} from './common.js';
import {
  buildingQueueItemSchema,
  portTradeSchema,
  resourceBlockSchema,
  tradingPortTermsSchema,
  upgradeOptionSchema,
} from './economy.js';
import { intelSnapshotSchema } from './intel.js';
import {
  fleetDetectionSchema,
  habitatSchema,
  shipOptionSchema,
  shipProductionItemSchema,
} from './military.js';

/** G-60: 3–24 characters, letters (any script), digits and single spaces. */
export const SYSTEM_NAME_MIN_LENGTH = 3;
export const SYSTEM_NAME_MAX_LENGTH = 24;
export const systemNameSchema = z
  .string()
  .trim()
  .min(SYSTEM_NAME_MIN_LENGTH)
  .max(SYSTEM_NAME_MAX_LENGTH)
  .regex(/^[\p{L}\p{N}]+( [\p{L}\p{N}]+)*$/u, 'letters, digits and single spaces');

/** `null` restores the generated name. */
export const renameSystemRequestSchema = z.strictObject({ name: systemNameSchema.nullable() });
export type RenameSystemRequest = z.infer<typeof renameSystemRequestSchema>;
export const renameSystemResponseSchema = z.strictObject({
  systemId: idSchema,
  name: z.string(),
  generatedName: z.string(),
});
export type RenameSystemResponse = z.infer<typeof renameSystemResponseSchema>;

export const systemOwnerSchema = z.strictObject({
  playerId: idSchema,
  displayName: z.string(),
  alliance: z
    .strictObject({ id: idSchema, tag: z.string(), emblem: allianceEmblemSchema.nullable() })
    .nullable(),
});

const publicSystemFields = {
  id: idSchema,
  name: z.string(),
  /** The name the galaxy generated; differs from `name` once the owner renamed it (G-60). */
  generatedName: z.string(),
  sectorId: idSchema,
  x: z.number(),
  y: z.number(),
  systemType: systemTypeSchema,
  planets: z.strictObject({
    orePlanets: z.number().int().nonnegative(),
    gasGiants: z.number().int().nonnegative(),
    earthLikes: z.number().int().nonnegative(),
  }),
  owner: systemOwnerSchema.nullable(),
  isCitadel: z.boolean(),
  /** Boolean only: the remaining shield time is private (GDD 06 §1.4). */
  ownerShielded: z.boolean(),
  /** Sum of the system's building levels (G-77, 16c feedback); 0 when unowned. */
  points: z.number().int().nonnegative(),
};

export const publicSystemSchema = z.strictObject({
  ...publicSystemFields,
  warpConnections: z.array(z.strictObject({ systemId: idSchema, distanceLy: z.number() })),
});
export type PublicSystem = z.infer<typeof publicSystemSchema>;

/** Stored Influence of an owned system (whole units). The boost is cut from the slice (S-3). */
export const influenceBlockSchema = z.strictObject({
  stored: z.number().int().nonnegative(),
  perHour: z.number().int().nonnegative(),
  cap: z.number().int().nonnegative(),
});
export type InfluenceBlock = z.infer<typeof influenceBlockSchema>;

/**
 * `full` tier — slice subset up to M3: facilities with the next upgrade of each, resources with rates
 * and caps, stored Influence, the building queue, the Trading Port, stationed fleets, ship production
 * with the classes the system can build, Habitat, the active battle, incoming detections and the open
 * claims on the system (owner view, 04 §3.6). Research is added when it leaves the cut list.
 */
export const fullSystemSchema = z.strictObject({
  ...publicSystemSchema.shape,
  facilities: z.partialRecord(facilityTypeSchema, z.number().int().min(0).max(20)),
  upgrades: z.partialRecord(facilityTypeSchema, upgradeOptionSchema),
  resources: resourceBlockSchema,
  influence: influenceBlockSchema,
  buildingQueue: z.array(buildingQueueItemSchema),
  portTrade: portTradeSchema.nullable(),
  tradingPort: tradingPortTermsSchema,
  fleets: z.array(
    z.strictObject({
      id: idSchema,
      isGarrison: z.boolean(),
      ownerPlayerId: idSchema.nullable(),
      ships: shipCountsSchema,
      stealthShips: shipCountsSchema,
    })
  ),
  shipProduction: z.array(shipProductionItemSchema),
  shipOptions: z.partialRecord(shipTypeSchema, shipOptionSchema),
  habitat: habitatSchema,
  activeBattleId: idSchema.nullable(),
  incomingDetections: z.array(fleetDetectionSchema),
  claims: z.array(ownerClaimSchema),
});
export type FullSystem = z.infer<typeof fullSystemSchema>;

/**
 * `intel` tier (04 §3.3): public fields plus the newest intel report the viewer may read — a snapshot
 * labelled with its time, never live data (V-5).
 */
export const intelSystemSchema = z.strictObject({
  ...publicSystemSchema.shape,
  intel: z.strictObject({
    reportId: idSchema,
    takenAt: isoDateTimeSchema,
    scoutedBy: z.strictObject({ playerId: idSchema, displayName: z.string() }),
    snapshot: intelSnapshotSchema,
  }),
});
export type IntelSystem = z.infer<typeof intelSystemSchema>;

export const systemResponseSchema = z.discriminatedUnion('tier', [
  z.strictObject({ tier: z.literal('public'), system: publicSystemSchema }),
  z.strictObject({ tier: z.literal('intel'), system: intelSystemSchema }),
  z.strictObject({ tier: z.literal('full'), system: fullSystemSchema }),
]);
export type SystemResponse = z.infer<typeof systemResponseSchema>;

export const sectorSchema = z.strictObject({
  id: idSchema,
  name: z.string(),
  centerX: z.number(),
  centerY: z.number(),
  radius: z.number(),
});

export const mapSystemSchema = z.strictObject(publicSystemFields);
export type MapSystem = z.infer<typeof mapSystemSchema>;

export const mapResponseSchema = z.strictObject({
  /** Changes whenever public map data changes; also the ETag. */
  version: z.string(),
  sectors: z.array(sectorSchema),
  systems: z.array(mapSystemSchema),
  warpConnections: z.array(z.strictObject({ a: idSchema, b: idSchema, distanceLy: z.number() })),
  wormholes: z.array(z.strictObject({ a: idSchema, b: idSchema })),
});
export type MapResponse = z.infer<typeof mapResponseSchema>;
